import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  Lening,
  OmschrijvingEnBedrag
} from "@snelstart-ventures/fiscaal/domain-logic";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-financieringskosten',
  templateUrl: './financieringskosten.component.html',
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ],
  styleUrls: ['./financieringskosten.component.scss']
})
export class FinancieringskostenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public dialogVisible = false;

  public formGroup = new FormGroup({
    financieringskosten: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  public soortFinancieringskosten: SelectFormControlItem<string>[] = [
    {value: 'Taxatiekosten', label: "Taxatiekosten"},
    {value: 'Notariskosten', label: "Notariskosten"},
    {value: 'Bemiddelingskosten', label: "Bemiddelingskosten"},
    {value: 'Kosten NHG', label: "Kosten NHG"},
    {value: 'Overige kosten', label: "Overige kosten"},
  ];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe((data) => {
        this.updateForm(
          data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.leningen?.[0]?.kosten || []
        );
      });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () => this.financieringskosten.getRawValue().map(w => ({
      bedrag: w.bedrag,
    }));

    this.changeSubscriptions.add(
      this.financieringskosten.valueChanges.pipe(
        debounceTime(500),
        tap(() => this.saveForm()),
        map(() => extractRelevant()),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        ),
        switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe()
    );
  }

  get financieringskosten(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.financieringskosten;
  }

  addRij(): void {
    this.financieringskosten.push(this.createFormGroup(), {emitEvent: false});
  }

  deleteRij(index: number): void {
    this.financieringskosten.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  private createFormGroup(item?: OmschrijvingEnBedrag): OmschrijvingBedragFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  private updateForm(data: OmschrijvingEnBedrag[]): void {
    const lijst = data || [];
    const formArray = this.financieringskosten;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const financieringskosten: OmschrijvingEnBedrag[] = this.financieringskosten.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const bestaandeLeningen = currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning?.leningen ?? [];

      if (bestaandeLeningen.length < 1) {
        return currentEntity;
      }

      const leningen: Lening[] = bestaandeLeningen.map((lening, index) =>
        index === 0 ? {
            ...lening,
            kosten: financieringskosten.length ? financieringskosten : undefined,
          }
          : lening
      );

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box1: {
            ...currentEntity.aangifte.box1,
            belastbareInkomstenUitEigenWoning: {
              ...currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning,
              leningen: leningen,
            }
          }
        },
      } as IncomeTaxEntity;
    });
  }
}
